import { useState } from "react";
import { X, Wrench, Send, Loader2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = "/api";

export default function CustomPartModal({ isOpen, onClose }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", firearm_make: "", firearm_model: "",
    firearm_year: "", firearm_era: "", firearm_variant: "", part_needed: "",
    has_original: "no", additional_info: ""
  });

  function handleChange(e) {
    setFormData(function(prev) { return { ...prev, [e.target.name]: e.target.value }; });
  }

  function handleSubmit(e) {
    e.preventDefault();
    setIsSubmitting(true);
    axios.post(API + "/custom-part-request", formData)
      .then(function() {
        setIsSubmitted(true);
        toast.success("Custom part request submitted!");
      })
      .catch(function() { toast.error("Failed to submit. Try again."); })
      .finally(function() { setIsSubmitting(false); });
  }

  function handleClose() {
    if (isSubmitted) {
      setFormData({ first_name: "", last_name: "", email: "", firearm_make: "", firearm_model: "", firearm_year: "", firearm_era: "", firearm_variant: "", part_needed: "", has_original: "no", additional_info: "" });
      setIsSubmitted(false);
    }
    onClose();
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/80 z-[60]" onClick={handleClose} />
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-[#0a0a0a] border border-white/10 rounded-sm z-[60] max-h-[90vh] overflow-y-auto" data-testid="custom-part-modal">
        <div className="sticky top-0 p-6 border-b border-white/10 flex items-center justify-between bg-[#0a0a0a] z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#C0A062]/10 flex items-center justify-center">
              <Wrench className="w-5 h-5 text-[#C0A062]" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-white text-xl uppercase">Request Custom Part</h2>
              <p className="text-xs text-[#A1A1AA]">Tell us what you need</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-[#A1A1AA] hover:text-white"><X className="w-6 h-6" /></button>
        </div>

        <div className="p-6">
          {isSubmitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#4A5D23]/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-[#8CB369]" />
              </div>
              <h3 className="font-heading font-bold text-white text-2xl uppercase mb-4">Request Received!</h3>
              <p className="text-[#A1A1AA] mb-6">We'll review and get back to you within 2-3 business days.</p>
              <Button className="btn-secondary" onClick={handleClose}>Close</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="custom-part-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">First Name *</Label>
                  <Input name="first_name" value={formData.first_name} onChange={handleChange} required className="input-tactical" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Last Name *</Label>
                  <Input name="last_name" value={formData.last_name} onChange={handleChange} required className="input-tactical" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Email *</Label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} required className="input-tactical" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Firearm Make *</Label>
                  <Input name="firearm_make" value={formData.firearm_make} onChange={handleChange} required placeholder="e.g., Mauser, Maxim" className="input-tactical" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA] text-xs uppercase">Model *</Label>
                  <Input name="firearm_model" value={formData.firearm_model} onChange={handleChange} required placeholder="e.g., MG-08, PKM" className="input-tactical" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Part Needed *</Label>
                <Input name="part_needed" value={formData.part_needed} onChange={handleChange} required placeholder="e.g., Firing Pin, Trunnion" className="input-tactical" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#A1A1AA] text-xs uppercase">Additional Info</Label>
                <Textarea name="additional_info" value={formData.additional_info} onChange={handleChange} placeholder="Year, variant, or other details..." className="input-tactical min-h-[100px] resize-none" />
              </div>
              <Button type="submit" className="btn-primary w-full" disabled={isSubmitting} data-testid="custom-part-submit">
                {isSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Submitting...</> : <><Send className="w-4 h-4 mr-2" />Submit Request</>}
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
